/**
 * MERGE ASSETS — image loader for merge objects (pickups + flask bodies).
 */
import * as C from "../config/constants.js";
import { mergeTierImageUrl } from "./merge-tier-urls.js";
import { getCachedImage, preloadImageUrl, preloadImageUrls } from "./image-cache.js";
import { runtimeFlags } from "../core/runtime-flags.js";

var DEFAULT_IMAGE_BODY_RADIUS_RATIO = 0.45;

/** Tier slice for preload (matches `MAX_MERGE_EVOLUTION_TIERS`). */
function evolutionChainForPreload() {
  var chain = C.EVOLUTION_CHAIN || [];
  var cap = C.MAX_MERGE_EVOLUTION_TIERS;
  var n = chain.length;
  if (cap != null) {
    var parsed = Math.floor(Number(cap));
    if (isFinite(parsed)) {
      n = Math.max(1, Math.min(chain.length, parsed));
    }
  }
  return chain.slice(0, n);
}

function getTierConfig(tier) {
  var chain = C.EVOLUTION_CHAIN;
  if (!chain || !chain[tier]) return null;
  return chain[tier];
}

function getAssetId(tier) {
  var cfg = getTierConfig(tier);
  return cfg ? (cfg.assetId || null) : null;
}

function getTierImage(assetId) {
  if (!assetId) return null;
  return getCachedImage(mergeTierImageUrl(assetId));
}

/**
 * Sprite layout for a merge body (Pixi or Canvas). The `assetCenter` point sits at (cx, cy).
 * @returns {{
 *   image: CanvasImageSource,
 *   x: number,
 *   y: number,
 *   width: number,
 *   height: number,
 *   rotation: number,
 *   anchorX: number,
 *   anchorY: number,
 *   physicsAngle: number,
 *   radiusX: number,
 *   radiusY: number,
 * } | null}
 */
function layoutMergeObject(cx, cy, radius, tier, angle, radiusY) {
  var radiusX = radius;
  if (radiusY == null) radiusY = radiusX;
  var maxRadius = Math.max(radiusX, radiusY);
  var cfg = getTierConfig(tier);
  var ellipseAngle = (cfg && cfg.ellipseAngle != null) ? cfg.ellipseAngle : 0;
  var imageAngle = angle || 0;
  var assetId = getAssetId(tier);
  var img = getTierImage(assetId);
  if (!img || !(img.naturalWidth > 0) || !(img.naturalHeight > 0)) return null;
  var center = (cfg && cfg.assetCenter) || [0.5, 0.5];
  var ratio = (cfg && cfg.imageBodyRadiusRatio != null) ? cfg.imageBodyRadiusRatio : DEFAULT_IMAGE_BODY_RADIUS_RATIO;
  var iw = img.naturalWidth;
  var ih = img.naturalHeight;
  var maxSize = maxRadius / ratio;
  var scale = maxSize / Math.max(iw, ih);
  return {
    image: img,
    x: cx,
    y: cy,
    width: iw * scale,
    height: ih * scale,
    rotation: imageAngle,
    anchorX: center[0],
    anchorY: center[1],
    physicsAngle: imageAngle + ellipseAngle,
    radiusX: radiusX,
    radiusY: radiusY,
  };
}

function drawMergeObject(ctx, cx, cy, radius, tier, fallbackColor, angle, radiusY) {
  var radiusX = radius;
  if (radiusY == null) radiusY = radiusX;
  var maxRadius = Math.max(radiusX, radiusY);
  var isEllipse = Math.abs(radiusX - radiusY) > 0.01;
  var cfg = getTierConfig(tier);
  var ellipseAngle = (cfg && cfg.ellipseAngle != null) ? cfg.ellipseAngle : 0;
  var imageAngle = angle || 0;
  var physicsAngle = imageAngle + ellipseAngle;
  var assetId = getAssetId(tier);
  var img = getTierImage(assetId);

  if (img) {
    var cfg2 = getTierConfig(tier);
    var center = (cfg2 && cfg2.assetCenter) || [0.5, 0.5];
    var cxRatio = center[0];
    var cyRatio = center[1];
    var ratio = (cfg2 && cfg2.imageBodyRadiusRatio != null) ? cfg2.imageBodyRadiusRatio : DEFAULT_IMAGE_BODY_RADIUS_RATIO;

    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    var maxSize = maxRadius / ratio;
    var scale = maxSize / Math.max(iw, ih);
    var drawW = iw * scale;
    var drawH = ih * scale;

    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.translate(cx, cy);
    if (imageAngle) ctx.rotate(imageAngle);
    ctx.translate(-cxRatio * drawW, -cyRatio * drawH);
    ctx.drawImage(img, 0, 0, iw, ih, 0, 0, drawW, drawH);
    ctx.restore();
    if (runtimeFlags.showPhysicsBodyContour) {
      ctx.strokeStyle = runtimeFlags.testFlaskMode ? "#00aaff" : "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (isEllipse) {
        ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
      }
      ctx.stroke();
    }
    return true;
  } else {
    ctx.fillStyle = fallbackColor || "#94a3b8";
    ctx.beginPath();
    if (isEllipse) {
      ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
    } else {
      ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
    }
    ctx.fill();
    if (runtimeFlags.showPhysicsBodyContour) {
      ctx.strokeStyle = runtimeFlags.testFlaskMode ? "#00aaff" : "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (isEllipse) {
        ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
      }
      ctx.stroke();
    }
    return false;
  }
}

function preload(tiers) {
  var chain = C.EVOLUTION_CHAIN;
  if (!chain) return Promise.resolve();
  var active = evolutionChainForPreload();
  var toLoad = tiers || active.map(function (_, i) { return i; });
  var urls = [];
  var seen = {};
  for (var i = 0; i < toLoad.length; i++) {
    var aid = getAssetId(toLoad[i]);
    if (!aid || seen[aid]) continue;
    seen[aid] = true;
    var url = mergeTierImageUrl(aid);
    if (url) urls.push(url);
  }
  return preloadImageUrls(urls);
}

/** Load evolution images for active tiers only (see `MAX_MERGE_EVOLUTION_TIERS`). */
function preloadAll() {
  var chain = evolutionChainForPreload();
  if (!chain || chain.length === 0) return Promise.resolve([]);
  var seen = {};
  var urls = [];
  for (var i = 0; i < chain.length; i++) {
    var assetId = chain[i] && chain[i].assetId;
    if (!assetId || seen[assetId]) continue;
    seen[assetId] = true;
    var url = mergeTierImageUrl(assetId);
    if (url) urls.push(url);
  }
  return preloadImageUrls(urls);
}

function ensureLoaded(tier, callback) {
  var assetId = getAssetId(tier);
  if (!assetId) {
    if (callback) callback(null);
    return Promise.resolve(null);
  }
  var cached = getTierImage(assetId);
  if (cached) {
    if (callback) callback(cached);
    return Promise.resolve(cached);
  }
  return preloadImageUrl(mergeTierImageUrl(assetId)).then(function (img) {
    if (callback) callback(img);
    return img;
  });
}

export const MergeAssets = {
  draw: drawMergeObject,
  layout: layoutMergeObject,
  preload: preload,
  preloadAll: preloadAll,
  ensureLoaded: ensureLoaded,
  DEFAULT_IMAGE_BODY_RADIUS_RATIO: DEFAULT_IMAGE_BODY_RADIUS_RATIO,
};